import type { CostObjectApprovalRecord, DormantKeyNotification } from '@/types/parley';

// Read state for the header bell. The API derives every notification live
// from a condition that is still true (a request still pending, a key still
// paused for dormancy), so nothing server side ever clears one: a paused key
// stayed in the bell until someone unpaused it. This keeps a per-user set of
// notification ids the user has viewed or marked read, in localStorage like
// the welcome-dialog claims (usePendingWelcome.ts), with an in-session
// fallback when storage is unavailable. Device-local by design, same as those
// claims.
//
// An id names the *occurrence*, not just the subject: it carries the state
// and the timestamp the API reports, so a key that is warned again later, or
// paused after a warning, comes back as unread.
const READ_PREFIX = 'parley.notifications.read.v1';

const inSession = new Map<string, Set<string>>();

function storageKeyFor(kerberos: string): string {
  return `${READ_PREFIX}.${kerberos || 'unknown'}`;
}

export function approvalNotificationId(notification: CostObjectApprovalRecord): string {
  return `approval:${notification.accountId}:${notification.createdAt}`;
}

export function dormantKeyNotificationId(notification: DormantKeyNotification): string {
  return `dormant:${notification.accountId}:${notification.hashedApiKey}:${notification.state}:${notification.since ?? ''}`;
}

export type NotificationKind = 'approval' | 'dormant';

export function notificationKindOf(id: string): NotificationKind | undefined {
  if (id.startsWith('approval:')) return 'approval';
  if (id.startsWith('dormant:')) return 'dormant';
  return undefined;
}

export function loadReadNotificationIds(kerberos: string): Set<string> {
  const storageKey = storageKeyFor(kerberos);
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const ids = new Set(parsed.filter((id): id is string => typeof id === 'string'));
        inSession.set(storageKey, ids);
        return new Set(ids);
      }
    }
  } catch {
    // localStorage unavailable or the value is not ours: the in-session copy
    // is all we have.
  }
  return new Set(inSession.get(storageKey) ?? []);
}

export function saveReadNotificationIds(kerberos: string, ids: Iterable<string>): void {
  const storageKey = storageKeyFor(kerberos);
  const next = new Set(ids);
  inSession.set(storageKey, next);
  try {
    localStorage.setItem(storageKey, JSON.stringify([...next]));
  } catch {
    // Fall through on the in-session copy alone.
  }
}
